import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  MenuItem,
  Grid,
  Snackbar,
  Alert,
  Chip,
  Divider,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import JsBarcode from "jsbarcode";

import Header from "../../components/Header";
import { authGet, authPost, authPatch, unwrapRows } from "../../lib/authApi";

function pickRows(resp) {
  if (!resp) return [];
  if (Array.isArray(resp)) return resp;
  if (Array.isArray(resp.rows)) return resp.rows;
  if (Array.isArray(resp.data)) return resp.data;
  return unwrapRows(resp) || [];
}

function fullPatientName(p) {
  if (!p) return "—";
  return p.full_name || [p.first_name, p.last_name].filter(Boolean).join(" ") || "—";
}

function statusColor(status) {
  const s = String(status || "").toLowerCase();
  if (s === "completed") return "success";
  if (s === "received" || s === "in_process") return "info";
  if (s === "printed" || s === "collected") return "warning";
  if (s === "cancelled") return "error";
  return "default";
}

export default function BarcodeGenerator() {
  const { search } = useLocation();
  const navigate = useNavigate();
  const qs = useMemo(() => new URLSearchParams(search), [search]);

  const svgRef = useRef(null);

  const [toast, setToast] = useState({
    open: false,
    msg: "",
    severity: "success",
  });

  const [orgs, setOrgs] = useState([]);
  const [patients, setPatients] = useState([]);

  const [orgId, setOrgId] = useState(qs.get("org") || "");
  const [patientId, setPatientId] = useState(qs.get("patient") || "");
  const [trackingId, setTrackingId] = useState(qs.get("tracking") || "");

  const [specimenSource, setSpecimenSource] = useState("");
  const [sampleType, setSampleType] = useState("");
  const [notes, setNotes] = useState("");

  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);
  const [selectedPatient, setSelectedPatient] = useState(null);

  // Load organizations
  useEffect(() => {
    let cancelled = false;

    async function loadOrgs() {
      try {
        const j = await authGet("/api/organizations");
        const list = j?.organizations || j?.rows || pickRows(j) || [];

        if (cancelled) return;
        setOrgs(list);

        if (!orgId && list.length) {
          setOrgId(list[0].id);
        }
      } catch (e) {
        if (!cancelled) {
          setOrgs([]);
          setToast({
            open: true,
            msg: `Failed to load organizations: ${e.message}`,
            severity: "error",
          });
        }
      }
    }

    loadOrgs();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load patients for selected org
  useEffect(() => {
    let cancelled = false;

    async function loadPatients() {
      if (!orgId) {
        setPatients([]);
        return;
      }

      try {
        const j = await authGet(`/api/patients?org_id=${encodeURIComponent(orgId)}&limit=2000`);
        const list = j?.patients || pickRows(j) || [];

        if (!cancelled) {
          setPatients(list);
        }
      } catch (e) {
        if (!cancelled) {
          setPatients([]);
          setToast({
            open: true,
            msg: `Failed to load patients: ${e.message}`,
            severity: "error",
          });
        }
      }
    }

    loadPatients();
    return () => {
      cancelled = true;
    };
  }, [orgId]);

  // Keep selected patient in sync
  useEffect(() => {
    const p = patients.find((x) => x.id === patientId) || null;
    setSelectedPatient(p);
  }, [patients, patientId]);

  // Load an existing tracking record if query includes ?tracking=
  useEffect(() => {
    let cancelled = false;

    async function loadTracking() {
      if (!orgId || !trackingId) return;

      try {
        const j = await authGet(
          `/api/specimen-tracking/${encodeURIComponent(trackingId)}?org_id=${encodeURIComponent(orgId)}`
        );

        const row = j?.row;
        if (!row || cancelled) return;

        setCreated(row);
        setPatientId(row.patient_id || "");
        setSpecimenSource(row.specimen_source || "");
        setSampleType(row.sample_type || "");
        setNotes(row.notes || "");
      } catch (e) {
        if (!cancelled) {
          setToast({
            open: true,
            msg: `Failed to load tracking record: ${e.message}`,
            severity: "error",
          });
        }
      }
    }

    loadTracking();
    return () => {
      cancelled = true;
    };
  }, [orgId, trackingId]);

  // Render barcode when created changes
  useEffect(() => {
    if (!created?.tracking_code || !svgRef.current) return;

    try {
      JsBarcode(svgRef.current, created.tracking_code, {
        format: "CODE128",
        width: 2,
        height: 72,
        displayValue: true,
        margin: 8,
        fontSize: 18,
      });
    } catch (e) {
      setToast({
        open: true,
        msg: `Failed to render barcode: ${e.message}`,
        severity: "error",
      });
    }
  }, [created]);

  const syncUrl = (next = {}) => {
    const url = new URL(window.location.href);

    const finalOrg = next.orgId ?? orgId;
    const finalPatient = next.patientId ?? patientId;
    const finalTracking = next.trackingId ?? trackingId;

    if (finalOrg) url.searchParams.set("org", finalOrg);
    else url.searchParams.delete("org");

    if (finalPatient) url.searchParams.set("patient", finalPatient);
    else url.searchParams.delete("patient");

    if (finalTracking) url.searchParams.set("tracking", finalTracking);
    else url.searchParams.delete("tracking");

    navigate(`${url.pathname}?${url.searchParams.toString()}`, { replace: true });
  };

  const generate = async () => {
    if (!orgId) {
      setToast({ open: true, msg: "Select an organization.", severity: "error" });
      return;
    }

    if (!patientId) {
      setToast({ open: true, msg: "Select a patient.", severity: "error" });
      return;
    }

    setBusy(true);
    try {
      const resp = await authPost("/api/specimen-tracking/generate", {
        org_id: orgId,
        patient_id: patientId,
        specimen_source: specimenSource || null,
        sample_type: sampleType || null,
        notes: notes || null,
      });

      const row = resp?.row || null;
      if (!row) throw new Error("No tracking record returned");

      setCreated(row);
      setTrackingId(row.id || "");
      syncUrl({ trackingId: row.id || "", patientId, orgId });

      setToast({
        open: true,
        msg: "Barcode generated.",
        severity: "success",
      });
    } catch (e) {
      setToast({
        open: true,
        msg: `Failed to generate barcode: ${e.message}`,
        severity: "error",
      });
    } finally {
      setBusy(false);
    }
  };

  const markPrinted = async () => {
    if (!created?.id || !orgId) return;

    try {
      const resp = await authPatch(
        `/api/specimen-tracking/${encodeURIComponent(created.id)}/status`,
        {
          org_id: orgId,
          status: "printed",
          notes: notes || null,
        }
      );

      if (resp?.row) setCreated(resp.row);
    } catch {
      // non-blocking
    }
  };

  const printLabel = async () => {
    if (!created || !svgRef.current) {
      setToast({
        open: true,
        msg: "Generate a barcode first.",
        severity: "error",
      });
      return;
    }

    const popup = window.open("", "_blank", "width=900,height=700");
    if (!popup) {
      setToast({
        open: true,
        msg: "Pop-up blocked. Allow pop-ups to print labels.",
        severity: "error",
      });
      return;
    }

    const patientName = fullPatientName(selectedPatient);
    const patientExternalId = selectedPatient?.patient_external_id || "—";
    const dob = selectedPatient?.dob
      ? new Date(selectedPatient.dob).toLocaleDateString()
      : "—";

    popup.document.write(`
      <html>
        <head>
          <title>Barcode Label</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              padding: 24px;
              color: #111;
            }
            .label {
              width: 620px;
              border: 1px solid #ddd;
              padding: 24px;
              border-radius: 8px;
            }
            .meta {
              margin-top: 14px;
              font-size: 16px;
              line-height: 1.5;
            }
            .meta div {
              margin-bottom: 6px;
            }
          </style>
        </head>
        <body>
          <div class="label">
            <h2>Specimen Tracking Label</h2>
            ${svgRef.current.outerHTML}
            <div class="meta">
              <div><strong>Tracking Code:</strong> ${created.tracking_code}</div>
              <div><strong>Patient:</strong> ${patientName}</div>
              <div><strong>Patient ID:</strong> ${patientExternalId}</div>
              <div><strong>DOB:</strong> ${dob}</div>
              <div><strong>Specimen Source:</strong> ${specimenSource || "—"}</div>
              <div><strong>Sample Type:</strong> ${sampleType || "—"}</div>
              <div><strong>Status:</strong> ${String(created.status || "").toUpperCase()}</div>
            </div>
          </div>
          <script>
            window.onload = function () {
              window.print();
            };
          </script>
        </body>
      </html>
    `);

    popup.document.close();
    await markPrinted();
  };

  const resetForm = () => {
    setTrackingId("");
    setCreated(null);
    setSpecimenSource("");
    setSampleType("");
    setNotes("");

    syncUrl({
      trackingId: "",
      patientId,
      orgId,
    });
  };

  return (
    <Box p={3} sx={{ pb: 6 }}>
      <Header
        title="Barcode Generator"
        subtitle="Create and print trackable specimen barcode labels"
      />

      <Grid container spacing={2} sx={{ mt: 0.5 }}>
        <Grid item xs={12} md={5}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                Generate Barcode
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Organization"
                    value={orgId || ""}
                    onChange={(e) => {
                      const nextOrg = e.target.value;
                      setOrgId(nextOrg);
                      setCreated(null);
                      setTrackingId("");
                      syncUrl({ orgId: nextOrg, patientId: "", trackingId: "" });
                      setPatientId("");
                    }}
                  >
                    {orgs.map((o) => (
                      <MenuItem key={o.id} value={o.id}>
                        {o.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Patient"
                    value={patientId || ""}
                    onChange={(e) => {
                      const nextPatient = e.target.value;
                      setPatientId(nextPatient);
                      syncUrl({ patientId: nextPatient, trackingId: "" });
                      setTrackingId("");
                      setCreated(null);
                    }}
                  >
                    <MenuItem value="">—</MenuItem>
                    {patients.map((p) => (
                      <MenuItem key={p.id} value={p.id}>
                        {fullPatientName(p)}{" "}
                        {p.patient_external_id ? `(${p.patient_external_id})` : ""}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    label="Specimen Source"
                    fullWidth
                    size="small"
                    value={specimenSource}
                    onChange={(e) => setSpecimenSource(e.target.value)}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    label="Sample Type"
                    fullWidth
                    size="small"
                    value={sampleType}
                    onChange={(e) => setSampleType(e.target.value)}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label="Notes"
                    fullWidth
                    size="small"
                    multiline
                    minRows={2}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                  />
                </Grid>
              </Grid>

              <Box display="flex" gap={1} mt={2} flexWrap="wrap">
                <Button variant="contained" onClick={generate} disabled={busy}>
                  {busy ? "Generating..." : "Generate Barcode"}
                </Button>

                <Button variant="outlined" onClick={resetForm}>
                  Reset
                </Button>

                <Button
                  variant="outlined"
                  onClick={() => navigate(`/patients?org=${encodeURIComponent(orgId)}`)}
                  disabled={!orgId}
                >
                  Go to Patients
                </Button>
              </Box>
            </CardContent>
          </Card>

          {selectedPatient ? (
            <Card sx={{ mt: 2 }}>
              <CardContent>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Patient Details
                </Typography>

                <Typography>
                  <strong>Name:</strong> {fullPatientName(selectedPatient)}
                </Typography>
                <Typography>
                  <strong>Patient ID:</strong> {selectedPatient.patient_external_id || "—"}
                </Typography>
                <Typography>
                  <strong>DOB:</strong>{" "}
                  {selectedPatient.dob
                    ? new Date(selectedPatient.dob).toLocaleDateString()
                    : "—"}
                </Typography>
                <Typography>
                  <strong>Gender:</strong> {selectedPatient.gender || "—"}
                </Typography>
                <Typography>
                  <strong>MRN:</strong> {selectedPatient.mrn || "—"}
                </Typography>
              </CardContent>
            </Card>
          ) : null}
        </Grid>

        <Grid item xs={12} md={7}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                Print Preview
              </Typography>

              {!created ? (
                <Typography color="text.secondary">
                  Generate a barcode to preview and print.
                </Typography>
              ) : (
                <Box>
                  <Box
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 2,
                      p: 3,
                      bgcolor: "white",
                    }}
                  >
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={2}
                    >
                      <Typography variant="h6">
                        Specimen Tracking Label
                      </Typography>
                      <Chip
                        label={String(created.status || "").toUpperCase()}
                        color={statusColor(created.status)}
                        variant="outlined"
                      />
                    </Box>

                    <svg ref={svgRef} />

                    <Divider sx={{ my: 2 }} />

                    <Typography sx={{ mb: 0.5 }}>
                      <strong>Tracking Code:</strong> {created.tracking_code}
                    </Typography>
                    <Typography sx={{ mb: 0.5 }}>
                      <strong>Patient:</strong> {fullPatientName(selectedPatient)}
                    </Typography>
                    <Typography sx={{ mb: 0.5 }}>
                      <strong>Patient ID:</strong> {selectedPatient?.patient_external_id || "—"}
                    </Typography>
                    <Typography sx={{ mb: 0.5 }}>
                      <strong>DOB:</strong>{" "}
                      {selectedPatient?.dob
                        ? new Date(selectedPatient.dob).toLocaleDateString()
                        : "—"}
                    </Typography>
                    <Typography sx={{ mb: 0.5 }}>
                      <strong>Specimen Source:</strong> {specimenSource || "—"}
                    </Typography>
                    <Typography sx={{ mb: 0.5 }}>
                      <strong>Sample Type:</strong> {sampleType || "—"}
                    </Typography>
                    <Typography sx={{ mb: 0.5 }}>
                      <strong>Created:</strong>{" "}
                      {created.created_at
                        ? new Date(created.created_at).toLocaleString()
                        : "—"}
                    </Typography>
                    <Typography sx={{ mb: 0.5 }}>
                      <strong>Printed:</strong>{" "}
                      {created.printed_at
                        ? new Date(created.printed_at).toLocaleString()
                        : "—"}
                    </Typography>
                  </Box>

                  <Box display="flex" gap={1} mt={2} flexWrap="wrap">
                    <Button variant="contained" onClick={printLabel}>
                      Print Label
                    </Button>

                    <Button
                      variant="outlined"
                      onClick={() => navigate(`/tracking?org=${encodeURIComponent(orgId)}`)}
                    >
                      Open Tracking Page
                    </Button>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
      >
        <Alert severity={toast.severity} variant="filled">
          {toast.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}